import React, { type FunctionComponent, useCallback } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { SVGIcon } from 'ic-snacks'
import { colors } from '../../../foundation/colors'
import { fontWeight } from '../../../foundation/fontWeight'
import { spacing } from '../../../foundation/spacing'
import { type ArrayTableMessages, type ArrayTableConfirmedColumn } from '../utils/ArrayTable.types'
import { fontSize } from '../../../foundation/fontSize'
import { Text } from '../../text/Text'
import { borderRadius } from '../../../foundation/borderRadius'

interface Props {
  columnIndex: number
  onColumnSelect: (columnIndex?: number) => void
  selectable?: boolean
  selectedColumn?: number
  confirmedColumn?: ArrayTableConfirmedColumn
  messages?: ArrayTableMessages
}

const confirmedStyles = css({ display: 'flex', alignItems: 'center' })
const confirmedMessageStyles = css({
  marginLeft: spacing.X8,
  fontSize: fontSize.X12,
  fontWeight: fontWeight.REGULAR,
})
const confirmedIconStyles = css({
  width: 20,
})

const SelectButton = styled.button({
  background: colors.GRAYSCALE.X0,
  borderRadius: borderRadius.X8,
  cursor: 'pointer',
  fontSize: fontSize.X12,
  fontWeight: fontWeight.SEMIBOLD,
  padding: `${spacing.X4} ${spacing.X8}`,

  '&:hover': {
    background: colors.GRAYSCALE.X10,
  },

  '&:active': {
    background: colors.GRAYSCALE.X20,
  },
})

const selectStyles = css({
  color: colors.SUCCESS.REGULAR,
})

const deselectStyles = css({
  color: colors.DETRIMENTAL.REGULAR,
})

export const ArrayTableColumnSelect: FunctionComponent<Props> = ({
  columnIndex,
  selectedColumn,
  onColumnSelect,
  confirmedColumn,
  selectable,
  messages,
}) => {
  const onDeselect = useCallback(() => onColumnSelect(), [onColumnSelect])
  const onSelect = useCallback(() => onColumnSelect(columnIndex), [onColumnSelect, columnIndex])

  if (confirmedColumn) {
    return (
      <div css={confirmedStyles} data-testid={`array-table-confirmed-${columnIndex}`}>
        <SVGIcon
          aria-hidden="true"
          focusable="false"
          className="icon"
          name="checkmarkCircleFilled"
          color={colors.SUCCESS.REGULAR}
          size="small"
          css={confirmedIconStyles}
        />
        <div css={confirmedMessageStyles}>
          <Text size="small">{confirmedColumn.message}</Text>
        </div>
      </div>
    )
  }

  if (!selectable) return null

  if (columnIndex === selectedColumn) {
    return (
      <SelectButton
        data-testid={`array-table-deselect-${columnIndex}`}
        css={deselectStyles}
        onClick={onDeselect}
      >
        {messages?.deselect}
      </SelectButton>
    )
  }

  return (
    <SelectButton
      data-testid={`array-table-select-${columnIndex}`}
      css={selectStyles}
      onClick={onSelect}
    >
      {messages?.select}
    </SelectButton>
  )
}
